import { useQuery } from '@tanstack/react-query';
import type { ColumnDef } from '@tanstack/react-table';
import { AlertTriangle, CalendarClock, Columns3, FileBarChart, FileSpreadsheet, PackageX } from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';
import { Link } from 'react-router';
import { DataTable, type DataTableState } from '@/components/DataTable';
import { WlasnyRaport } from '@/components/WlasnyRaport';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { granicaZPola } from '@/lib/czas-sps';
import { czasSps } from '@/lib/czas-sps';
import { LIMIT_EKSPORTU, zbierzWszystkieStrony, type FormatEksportu } from '@/lib/export';
import { listEmployees } from '@/lib/employees-api';
import { listGroups as listEmpGroups } from '@/lib/employee-groups-api';
import { useI18n } from '@/lib/i18n';
import { useIslands } from '@/lib/islands';
import { useOpoznione } from '@/lib/opoznienie';
import { listMachines } from '@/lib/machines-api';
import { cn } from '@/lib/utils';
import { fetchStanAutomatow, type PozycjaStanu } from '@/lib/reports-api';
import { fetchCentraKosztowe, listTransactions, type TransactionRow } from '@/lib/transactions-api';

const nfInt = new Intl.NumberFormat('pl-PL');
const nfMoney = new Intl.NumberFormat('pl-PL', { style: 'currency', currency: 'PLN' });

type Rodzaj = 'zatowarowanie' | 'wydania';

/**
 * Parametry, z którymi raport ZOSTAŁ ZLECONY — osobno od pól formularza.
 *
 * ⚠️ TO JEST SEDNO TEGO EKRANU, a nie szczegół implementacji. Zapytanie zależy WYŁĄCZNIE od tej
 * migawki, więc kręcenie filtrami niczego nie pobiera i niczego nie przerysowuje: raport zmienia
 * się dopiero po kliknięciu „Generuj". Poprzednia wersja odpytywała serwer od wejścia na ekran
 * i wysypywała wszystkie tabele naraz — użytkownik nazwał to ścianą tekstu i miał rację.
 */
type Zlecenie =
  | { rodzaj: 'zatowarowanie'; machineId: string }
  | {
      rodzaj: 'wydania';
      machineId: string;
      od: string;
      do: string;
      /** `HH:MM` albo pusty napis = cała doba. Patrz `granicaZPola`. */
      odGodz: string;
      doGodz: string;
      employeeId: string;
      costCenter: string | undefined;
      employeeGroupId: string;
    };

/** Pracownik na liście wyboru — tyle, ile pole naprawdę czyta. */
type PracownikDoWyboru = { id: string; name: string; spsUserNumber: string | null };

const opisPracownika = (p: PracownikDoWyboru): string =>
  p.spsUserNumber === null || p.spsUserNumber === '' ? p.name : `${p.name} · ${p.spsUserNumber}`;

/**
 * POLE PRACOWNIKA — jedno, nie dwa: wpisywanie JEST filtrem listy.
 *
 * ⚠️ ZASTĄPIŁO OSOBNĄ SZUKAJKĘ OBOK ROZWIJANEJ LISTY. Zgłoszone wprost: „nie chcę tego dodatkowego
 * pola z szukajką, tylko żeby filtrować bezpośrednio na tej rozwijanej liście". Dwa pola wymagały
 * zrozumienia, że jedno zawęża drugie — a wyglądały jak dwa niezależne filtry, więc wpisanie
 * nazwiska w szukajkę i nietknięcie listy dawało raport dla WSZYSTKICH, mimo widocznego nazwiska
 * na ekranie.
 *
 * ⚠️ NIE `<select>`, bo natywna lista nie umie filtrować, a przy kilkuset pracownikach jest
 * nieużywalna. Filtruje SERWER (nazwisko, numer karty, alias, centrum kosztowe) — lista z bazy
 * bywa dłuższa niż jedna strona, więc odsiew po stronie przeglądarki gubiłby dopasowania spoza niej.
 */
function PolePracownika({
  wpisane,
  onWpisane,
  wybrany,
  onWybrany,
  lista,
  laduje,
}: {
  wpisane: string;
  onWpisane: (v: string) => void;
  wybrany: string;
  onWybrany: (id: string) => void;
  lista: PracownikDoWyboru[];
  laduje: boolean;
}) {
  const { t } = useI18n();
  const [otwarte, setOtwarte] = useState(false);
  const osoba = lista.find((p) => p.id === wybrany);

  const wybierz = (id: string, tekst: string) => {
    onWybrany(id);
    onWpisane(tekst);
    setOtwarte(false);
  };

  return (
    <div className="relative">
      <Input
        value={wpisane}
        placeholder={osoba ? opisPracownika(osoba) : t('rap.allEmployees')}
        onChange={(e) => {
          onWpisane(e.target.value);
          setOtwarte(true);
          /**
           * ⚠️ PISANIE UNIEWAŻNIA WYBÓR. Bez tego pole pokazywałoby nowy tekst przy wciąż
           * wybranej starej osobie — czyli raport dotyczyłby kogoś, kogo nie widać na ekranie.
           */
          if (wybrany !== '') onWybrany('');
        }}
        onFocus={() => setOtwarte(true)}
        onBlur={() => setOtwarte(false)}
        onKeyDown={(e) => {
          if (e.key === 'Escape') setOtwarte(false);
        }}
      />
      {otwarte && (
        <div className="absolute z-20 mt-1 max-h-64 w-full overflow-y-auto rounded-md border border-border bg-[var(--popover,var(--card))] shadow-md">
          {/*
            ⚠️ `onMouseDown` z `preventDefault`, NIE `onClick`. Kliknięcie w pozycję najpierw
            zabiera fokus polu, więc `onBlur` zamknąłby listę, zanim `onClick` zdążyłby się
            wykonać — pozycje byłyby nieklikalne, a wyglądałyby normalnie.
          */}
          <button
            type="button"
            className="block w-full px-2.5 py-1.5 text-left text-xs hover:bg-accent"
            onMouseDown={(e) => {
              e.preventDefault();
              wybierz('', '');
            }}
          >
            {t('rap.allEmployees')}
          </button>
          {lista.map((p) => (
            <button
              key={p.id}
              type="button"
              className={cn(
                'block w-full px-2.5 py-1.5 text-left text-xs hover:bg-accent',
                p.id === wybrany && 'bg-accent',
              )}
              onMouseDown={(e) => {
                e.preventDefault();
                wybierz(p.id, opisPracownika(p));
              }}
            >
              {opisPracownika(p)}
            </button>
          ))}
          {!laduje && lista.length === 0 && (
            <div className="px-2.5 py-1.5 text-xs text-[var(--muted-foreground)]">{t('rap.employeeNone')}</div>
          )}
        </div>
      )}
    </div>
  );
}

function dzisiaj(): string {
  const d = new Date();
  const dwa = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${dwa(d.getMonth() + 1)}-${dwa(d.getDate())}`;
}

/** `Date` → `YYYY-MM-DD` w kalendarzu PATRZĄCEGO, bez przechodzenia przez UTC. */
function naPole(d: Date): string {
  const dwa = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${dwa(d.getMonth() + 1)}-${dwa(d.getDate())}`;
}

const oDni = (ile: number): Date => {
  const d = new Date();
  d.setDate(d.getDate() + ile);
  return d;
};

/**
 * SZYBKIE ZAKRESY DAT.
 *
 * ⚠️ TYDZIEŃ ZACZYNA SIĘ W PONIEDZIAŁEK. `getDay()` oddaje 0 dla NIEDZIELI, więc naiwne
 * `d.getDate() - d.getDay()` cofa do niedzieli — czyli w niedzielę „ten tydzień" pokazywałby
 * jeden dzień, a w poniedziałek siedem dni wstecz. U klienta tydzień pracy zaczyna się
 * w poniedziałek i raport ma to odwzorowywać.
 *
 * ⚠️ TE FUNKCJE WOLNO LICZYĆ PRZEZ `Date`, w odróżnieniu od znaczników wydań: to kalendarz
 * patrzącego („bieżący miesiąc"), a nie odczyt zegara automatu. Wynik i tak wychodzi jako
 * `YYYY-MM-DD`, bez strefy i bez godziny — patrz `czas-sps.ts`.
 */
const ZAKRESY = [
  { klucz: 'dzis', licz: (): [string, string] => [naPole(new Date()), naPole(new Date())] },
  { klucz: 'wczoraj', licz: (): [string, string] => [naPole(oDni(-1)), naPole(oDni(-1))] },
  {
    klucz: 'tydzien',
    licz: (): [string, string] => {
      const dzis = new Date();
      const poniedzialek = new Date(dzis);
      /** Niedziela (0) to SIÓDMY dzień tygodnia, nie zerowy — stąd ta poprawka. */
      poniedzialek.setDate(dzis.getDate() - ((dzis.getDay() + 6) % 7));
      return [naPole(poniedzialek), naPole(dzis)];
    },
  },
  {
    klucz: 'miesiac',
    licz: (): [string, string] => {
      const d = new Date();
      return [naPole(new Date(d.getFullYear(), d.getMonth(), 1)), naPole(d)];
    },
  },
  {
    klucz: 'poprzedniMiesiac',
    licz: (): [string, string] => {
      const d = new Date();
      /** ⚠️ Dzień 0 następnego miesiąca = OSTATNI dzień poprzedniego, bez tablicy długości. */
      return [naPole(new Date(d.getFullYear(), d.getMonth() - 1, 1)), naPole(new Date(d.getFullYear(), d.getMonth(), 0))];
    },
  },
] as const;

function przedMiesiacem(): string {
  const d = new Date();
  d.setMonth(d.getMonth() - 1);
  const dwa = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${dwa(d.getMonth() + 1)}-${dwa(d.getDate())}`;
}

export function Raportowanie() {
  const { t } = useI18n();
  const { groupId, island } = useIslands();

  const [rodzaj, setRodzaj] = useState<Rodzaj>('zatowarowanie');
  const [machineId, setMachineId] = useState('');
  const [od, setOd] = useState(przedMiesiacem());
  const [doDnia, setDoDnia] = useState(dzisiaj());
  /**
   * GODZINY — puste znaczy CAŁĄ DOBĘ.
   *
   * ⚠️ OSOBNE POLA, NIE `datetime-local`. Zwykłe pytanie brzmi „co pobrał w zeszłym tygodniu"
   * i wtedy godzina jest szumem, który trzeba by wpisywać za każdym razem. Godzina wchodzi
   * dopiero przy pytaniu w rodzaju „co pobierał Andrzej między 10 a 14 wczoraj" — więc jest
   * dodatkiem do daty, a nie jej częścią.
   */
  const [odGodz, setOdGodz] = useState('');
  const [doGodz, setDoGodz] = useState('');
  const [employeeId, setEmployeeId] = useState('');
  /**
   * Tekst wpisany w pole pracownika — JEDNOCZEŚNIE filtr listy i podpis wybranej osoby.
   * Operator ma w ręku albo nazwisko, albo KARTĘ, więc serwer szuka po obu (plus alias
   * i centrum kosztowe). Szczegóły zachowania w `PolePracownika`.
   */
  const [szukajPracownika, setSzukajPracownika] = useState('');
  /**
   * ⚠️ FRAZA DO ZAPYTANIA JEST OPÓŹNIONA, a pole reaguje od razu. Filtrowanie odbywa się na
   * serwerze, więc bez tego każde naciśnięcie klawisza to osobne wejście do bazy — przy pisaniu
   * nazwiska kilkanaście zapytań, z których liczy się ostatnie.
   */
  const frazaPracownika = useOpoznione(szukajPracownika, 250);
  const [costCenter, setCostCenter] = useState<string | undefined>(undefined);
  const [employeeGroupId, setEmployeeGroupId] = useState('');
  const [zlecenie, setZlecenie] = useState<Zlecenie | null>(null);

  const machines = useQuery({
    queryKey: ['machines', 'lista', groupId],
    queryFn: () =>
      listMachines({ groupId: groupId!, page: 1, pageSize: 200, search: '', sort: 'vmid', dir: 'asc', onlyOnline: false }),
    enabled: Boolean(groupId),
  });

  /** Listy do filtrów wolno pobierać od razu — to słowniki, nie raport. */
  const pracownicy = useQuery({
    queryKey: ['employees', 'do-raportu', groupId, frazaPracownika],
    queryFn: () =>
      listEmployees({
        groupId: groupId!,
        page: 1,
        pageSize: 500,
        ...(frazaPracownika.trim() !== '' ? { search: frazaPracownika.trim() } : {}),
        stan: 'wszyscy',
        onlyMultiMachine: false,
        sort: 'name',
        dir: 'asc',
      }),
    enabled: Boolean(groupId) && rodzaj === 'wydania',
  });

  const grupy = useQuery({
    queryKey: ['employee-groups', 'do-raportu', groupId],
    queryFn: () => listEmpGroups({ groupId: groupId!, page: 1, pageSize: 300, search: '', sort: 'name', dir: 'asc' }),
    enabled: Boolean(groupId) && rodzaj === 'wydania',
  });

  const centra = useQuery({
    queryKey: ['transactions', 'centra-kosztowe', groupId],
    queryFn: () => fetchCentraKosztowe(groupId!),
    enabled: Boolean(groupId) && rodzaj === 'wydania',
  });

  const generuj = useCallback(() => {
    if (rodzaj === 'zatowarowanie') {
      setZlecenie({ rodzaj, machineId });
      return;
    }
    setZlecenie({ rodzaj, machineId, od, do: doDnia, odGodz, doGodz, employeeId, costCenter, employeeGroupId });
  }, [rodzaj, machineId, od, doDnia, odGodz, doGodz, employeeId, costCenter, employeeGroupId]);

  /** Zmiana RODZAJU raportu chowa poprzedni wynik — inaczej zostałby pod formularzem innego raportu. */
  const zmienRodzaj = useCallback((r: Rodzaj) => {
    setRodzaj(r);
    setZlecenie(null);
  }, []);

  return (
    <div className="grid gap-6">
      <div>
        <h1 className="text-xl font-semibold">{t('rap.title')}</h1>
        <p className="text-sm text-[var(--muted-foreground)]">{t('rap.subtitle')}</p>
      </div>

      <section className="grid gap-4 rounded-lg border border-[var(--border)] bg-[var(--card)] p-4">
        <div className="grid gap-2">
          <span className="text-xs text-[var(--muted-foreground)]">{t('rap.kind')}</span>
          <div className="flex flex-wrap gap-2">
            <Button
              variant={rodzaj === 'zatowarowanie' ? 'default' : 'outline'}
              onClick={() => zmienRodzaj('zatowarowanie')}
            >
              {t('rap.kind.stock')}
            </Button>
            <Button variant={rodzaj === 'wydania' ? 'default' : 'outline'} onClick={() => zmienRodzaj('wydania')}>
              {t('rap.kind.issues')}
            </Button>
          </div>
          <p className="text-xs text-[var(--muted-foreground)]">
            {rodzaj === 'zatowarowanie' ? t('rap.kind.stockHint') : t('rap.kind.issuesHint')}
          </p>
        </div>

        {/*
          SZYBKIE ZAKRESY DAT — nad polami, bo zwykle to one wystarczają i wtedy do dat nie trzeba
          w ogóle schodzić.

          ⚠️ USTAWIAJĄ POLA, A NIE ZLECAJĄ RAPORTU. Ten ekran świadomie nie pobiera niczego, dopóki
          nie kliknie się „Generuj" (inaczej kręcenie filtrami wysypywało ścianę tabel) — więc
          skrót, który sam odpalałby zapytanie, łamałby tę zasadę dla jednego przypadku.

          ⚠️ GODZINY CZYŚCIMY przy zmianie zakresu. „Ten miesiąc" z godzinami 10–14 zostawionymi
          po poprzednim pytaniu dałoby raport z okna 10–14 KAŻDEGO dnia miesiąca — wynik pozornie
          sensowny i całkowicie mylący.
        */}
        {rodzaj === 'wydania' && (
          <div className="flex flex-wrap items-center gap-1.5">
            <span className="mr-1 text-xs text-[var(--muted-foreground)]">{t('rap.szybkiZakres')}</span>
            {ZAKRESY.map((z) => {
              const [a, b] = z.licz();
              const czynny = od === a && doDnia === b && odGodz === '' && doGodz === '';
              return (
                <button
                  key={z.klucz}
                  type="button"
                  onClick={() => {
                    setOd(a);
                    setDoDnia(b);
                    setOdGodz('');
                    setDoGodz('');
                  }}
                  aria-pressed={czynny}
                  className={cn(
                    'rounded-md border px-2 py-1 text-xs transition-colors',
                    czynny ? 'border-transparent bg-primary text-primary-foreground' : 'border-border hover:bg-accent',
                  )}
                >
                  {t(`rap.zakres.${z.klucz}`)}
                </button>
              );
            })}
          </div>
        )}

        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          <label className="grid gap-1 text-xs">
            {t('rap.machine')}
            <Select value={machineId} onChange={(e) => setMachineId(e.target.value)}>
              <option value="">{t('rap.allMachines')}</option>
              {(machines.data?.rows ?? []).map((m) => (
                <option key={m.id} value={m.id}>
                  {m.name}
                </option>
              ))}
            </Select>
          </label>

          {rodzaj === 'wydania' && (
            <>
              <label className="grid gap-1 text-xs">
                {t('rap.from')}
                <div className="flex gap-1">
                  <Input type="date" value={od} onChange={(e) => setOd(e.target.value)} />
                  {/* Godzina WĘŻSZA od daty — jest dodatkiem, a nie równorzędnym polem. */}
                  <Input
                    type="time"
                    className="w-24 shrink-0"
                    value={odGodz}
                    onChange={(e) => setOdGodz(e.target.value)}
                    title={t('rap.godzinaOd')}
                  />
                </div>
              </label>
              <label className="grid gap-1 text-xs">
                {t('rap.to')}
                <div className="flex gap-1">
                  <Input type="date" value={doDnia} onChange={(e) => setDoDnia(e.target.value)} />
                  <Input
                    type="time"
                    className="w-24 shrink-0"
                    value={doGodz}
                    onChange={(e) => setDoGodz(e.target.value)}
                    title={t('rap.godzinaDo')}
                  />
                </div>
              </label>
              <div className="grid gap-1 text-xs">
                <span>{t('rap.employee')}</span>
                <PolePracownika
                  wpisane={szukajPracownika}
                  onWpisane={setSzukajPracownika}
                  wybrany={employeeId}
                  onWybrany={setEmployeeId}
                  lista={pracownicy.data?.rows ?? []}
                  laduje={pracownicy.isFetching}
                />
                {/*
                  ⚠️ WPISANY TEKST TO JESZCZE NIE FILTR. Samo wpisanie nazwiska zawęża LISTĘ, ale
                  raport obejmuje wtedy wszystkich — i tak właśnie wygląda ekran, na którym ktoś
                  widzi nazwisko w polu i dostaje wydania całej firmy. To ta sama pomyłka, przez
                  którą zniknęła osobna szukajka; jedno pole ją zmniejszyło, ale nie usunęło.
                */}
                {szukajPracownika.trim() !== '' && employeeId === '' && (
                  <span className="text-[var(--muted-foreground)]">{t('rap.employeePickHint')}</span>
                )}
              </div>
              <label className="grid gap-1 text-xs">
                {t('tx.costCenter')}
                <Select
                  value={costCenter ?? '__wszystkie__'}
                  onChange={(e) => setCostCenter(e.target.value === '__wszystkie__' ? undefined : e.target.value)}
                >
                  <option value="__wszystkie__">{t('tx.allCostCenters')}</option>
                  {(centra.data ?? []).map((c) => (
                    <option key={c.wartosc} value={c.wartosc}>
                      {(c.wartosc === '' ? t('tx.noCostCenter') : c.wartosc) + ` (${nfInt.format(c.wydan)})`}
                    </option>
                  ))}
                </Select>
              </label>
              <label className="grid gap-1 text-xs">
                {t('rap.group')}
                <Select value={employeeGroupId} onChange={(e) => setEmployeeGroupId(e.target.value)}>
                  <option value="">{t('rap.allGroups')}</option>
                  {(grupy.data?.rows ?? []).map((g) => (
                    <option key={g.id} value={g.id}>
                      {g.name}
                    </option>
                  ))}
                </Select>
              </label>
            </>
          )}
        </div>

        <div className="flex flex-wrap items-center gap-2">
          <Button onClick={generuj} disabled={!groupId}>
            <FileBarChart className="size-4" />
            {t('rap.generate')}
          </Button>
          {/*
            ⚠️ WEJŚCIE DO HARMONOGRAMÓW STOI TUTAJ, a nie w menu głównym. To ten sam raport, tylko
            wysyłany bez klikania — osobna pozycja w nawigacji sugerowałaby osobną funkcję i drugie
            miejsce, w którym trzeba pamiętać o uprawnieniach.
          */}
          <Button variant="outline" asChild>
            <Link to="/raportowanie/harmonogramy">
              <CalendarClock className="size-4" />
              {t('rap.harmonogramy')}
            </Link>
          </Button>
          <Button variant="outline" asChild>
            <Link to="/raportowanie/sps">
              <FileSpreadsheet className="size-4" />
              {t('rsps.przycisk')}
            </Link>
          </Button>
        </div>
      </section>

      {zlecenie === null ? (
        <p className="text-sm text-[var(--muted-foreground)]">{t('rap.notGenerated')}</p>
      ) : zlecenie.rodzaj === 'zatowarowanie' ? (
        <RaportZatowarowania groupId={groupId!} zlecenie={zlecenie} kodWyspy={island?.code ?? 'wyspa'} />
      ) : (
        <RaportWydan groupId={groupId!} zlecenie={zlecenie} kodWyspy={island?.code ?? 'wyspa'} />
      )}
    </div>
  );
}

/* ───────────────────────────── RAPORT ZATOWAROWANIA ───────────────────────────── */

/**
 * ⚠️ JEDNA TABELA NARAZ, przełączana zakładkami. Poprzednia wersja rysowała podsumowanie ORAZ
 * tabelę na każdy automat pod nim — przy dwóch automatach DBW to trzy tabele po kilkadziesiąt
 * wierszy, czyli dokładnie ta ściana tekstu. Zakładki zachowują oba potrzebne widoki („ile wziąć
 * z magazynu" i „co włożyć w tym automacie") bez sypania obu naraz.
 */
function RaportZatowarowania({
  groupId,
  zlecenie,
  kodWyspy,
}: {
  groupId: string;
  zlecenie: { machineId: string };
  kodWyspy: string;
}) {
  const { t } = useI18n();
  const [widok, setWidok] = useState<string>('razem');

  const raport = useQuery({
    queryKey: ['raport', 'stan-automatow', groupId, zlecenie.machineId],
    queryFn: () => fetchStanAutomatow(groupId, zlecenie.machineId || undefined),
  });

  const dane = raport.data;
  const automat = dane?.automaty.find((a) => a.machineId === widok);
  const pozycje = widok === 'razem' ? (dane?.podsumowanie ?? []) : (automat?.pozycje ?? []);

  return (
    <div className="grid gap-4">
      <Swiezosc dane={dane} />

      {dane !== undefined && dane.pominietych > 0 && (
        <p className="text-xs text-[var(--muted-foreground)]">
          {t('rap.skipped')}: {nfInt.format(dane.pominietych)}
        </p>
      )}

      {dane !== undefined && (
        <div className="flex flex-wrap gap-2">
          <Button variant={widok === 'razem' ? 'default' : 'outline'} size="sm" onClick={() => setWidok('razem')}>
            {t('rap.summaryTitle')} ({nfInt.format(dane.brakuje)})
          </Button>
          {dane.automaty.map((a) => (
            <Button
              key={a.machineId}
              variant={widok === a.machineId ? 'default' : 'outline'}
              size="sm"
              onClick={() => setWidok(a.machineId)}
            >
              {a.name} ({nfInt.format(a.brakuje)})
            </Button>
          ))}
        </div>
      )}

      <TabelaStanu
        title={widok === 'razem' ? t('rap.summaryTitle') : (automat?.name ?? '')}
        opis={
          widok === 'razem'
            ? t('rap.summaryHint')
            : t('rap.machineHint')
                .replace('{brakuje}', nfInt.format(automat?.brakuje ?? 0))
                .replace('{zero}', nfInt.format(automat?.pozycjiNaZero ?? 0))
        }
        rows={pozycje}
        isLoading={raport.isPending}
        isFetching={raport.isFetching}
        onRefresh={() => void raport.refetch()}
        exportFileName={widok === 'razem' ? `do-zabrania-${kodWyspy}` : `stan-${automat?.name ?? kodWyspy}`}
        groupId={groupId}
        machineId={zlecenie.machineId}
        pokazMiejsca={widok !== 'razem'}
      />
    </div>
  );
}

/**
 * ŚWIEŻOŚĆ DANYCH — część raportu, nie ozdoba.
 *
 * ⚠️ RAPORT SPRZED TRZECH DNI WYGLĄDA IDENTYCZNIE JAK DZISIEJSZY, a człowiek ładuje na jego
 * podstawie samochód. Obecność agenta to przy tym NIE to samo co jego praca: melduje się co minutę
 * także wtedy, gdy nie może dojść do bazy klienta (zmierzone na DBW 2026-08-13 — dwadzieścia minut
 * poprawnych meldunków bez dostępu do bazy). Dlatego problem pokazujemy mocniej niż czas.
 */
function Swiezosc({
  dane,
}: {
  dane: { swiezosc: { nodeId: string | null; minutOdSygnalu: number | null; problem: string | null } } | undefined;
}) {
  const { t } = useI18n();
  if (dane === undefined) return null;
  const { nodeId, minutOdSygnalu, problem } = dane.swiezosc;

  if (nodeId === null) {
    return (
      <div className="flex items-start gap-2 rounded-md border border-[var(--warning)]/40 bg-[var(--warning)]/10 p-3 text-sm">
        <AlertTriangle className="mt-0.5 size-4 shrink-0 text-[var(--warning)]" />
        <span>{t('rap.noAgent')}</span>
      </div>
    );
  }

  if (problem !== null && problem !== '') {
    return (
      <div className="flex items-start gap-2 rounded-md border border-[var(--destructive)]/40 bg-[var(--destructive)]/10 p-3 text-sm">
        <AlertTriangle className="mt-0.5 size-4 shrink-0 text-[var(--destructive)]" />
        <div className="grid gap-1">
          <strong>{t('rap.agentProblem')}</strong>
          <span className="text-[var(--muted-foreground)]">{problem}</span>
        </div>
      </div>
    );
  }

  return (
    <p className="text-xs text-[var(--muted-foreground)]">
      {t('rap.fresh').replace('{node}', nodeId).replace('{minut}', minutOdSygnalu === null ? '—' : nfInt.format(minutOdSygnalu))}
    </p>
  );
}

/**
 * ⚠️ STRONICOWANIE I SORTOWANIE PO STRONIE PRZEGLĄDARKI, wyjątkowo: raport stanu przychodzi
 * w całości jednym zapytaniem, a eksport musi dostać KOMPLET, nie bieżącą stronę.
 */
function TabelaStanu({
  title,
  opis,
  rows,
  isLoading,
  isFetching,
  onRefresh,
  exportFileName,
  groupId,
  machineId,
  pokazMiejsca,
}: {
  title: string;
  opis: string;
  rows: PozycjaStanu[];
  isLoading: boolean;
  isFetching: boolean;
  onRefresh: () => void;
  exportFileName: string;
  groupId: string | null;
  /** Automat z FILTRA raportu, nie z bieżącej karty widoku — patrz komentarz przy `WlasnyRaport`. */
  machineId: string;
  pokazMiejsca: boolean;
}) {
  const { t } = useI18n();
  const [state, setState] = useState<DataTableState>({ search: '', page: 1, pageSize: 50, sort: '', dir: 'asc' });
  const onStateChange = useCallback((patch: Partial<DataTableState>) => setState((s) => ({ ...s, ...patch })), []);
  const [wlasny, setWlasny] = useState(false);

  const przefiltrowane = useMemo(() => {
    const szukane = state.search.trim().toLowerCase();
    const podzbior =
      szukane === ''
        ? rows
        : rows.filter(
            (r) => r.itemName.toLowerCase().includes(szukane) || r.itemNumber.toLowerCase().includes(szukane),
          );
    if (state.sort === '') return podzbior;
    const kier = state.dir === 'asc' ? 1 : -1;
    return [...podzbior].sort((a, b) => {
      const x = a[state.sort as keyof PozycjaStanu];
      const y = b[state.sort as keyof PozycjaStanu];
      if (typeof x === 'number' && typeof y === 'number') return (x - y) * kier;
      return String(x ?? '').localeCompare(String(y ?? ''), 'pl') * kier;
    });
  }, [rows, state.search, state.sort, state.dir]);

  const strona = useMemo(
    () => przefiltrowane.slice((state.page - 1) * state.pageSize, state.page * state.pageSize),
    [przefiltrowane, state.page, state.pageSize],
  );

  const columns = useMemo<ColumnDef<PozycjaStanu>[]>(
    () => [
      {
        id: 'itemName',
        header: t('rap.col.item'),
        accessorFn: (r) => r.itemName,
        cell: ({ row }) => (
          <div className="grid">
            <span>{row.original.itemName}</span>
            <span className="text-xs text-[var(--muted-foreground)]">{row.original.itemNumber}</span>
          </div>
        ),
        meta: { sortKey: 'itemName', exportValue: (r: PozycjaStanu) => r.itemName },
      },
      {
        /**
         * NUMER CZĘŚCI DOSTAWCY — `Items.SUPPLIERPARTNUMBER`, nazwa po sps-owemu.
         *
         * ⚠️⚠️ BRAKOWAŁO GO TU I DAWAŁO DWA OBJAWY NARAZ (zgłoszone 31.08.2026): kolumny nie było
         * na ekranie ANI w pobranym pliku `.txt`. Druga część nie jest osobnym błędem: przycisk
         * eksportu przy liście składa plik z KOLUMN EKRANU (`meta.exportValue` w `DataTable`),
         * więc kolumna dołożona wyłącznie do katalogu raportu serwerowego nie miała jak się w nim
         * pojawić. Kolejność ta sama co w katalogu (`KOLUMNY_STANU`) — zaraz po towarze.
         */
        id: 'supplierPartNumber',
        header: t('rap.col.supplierPart'),
        accessorFn: (r: PozycjaStanu) => r.supplierPartNumber ?? '',
        enableSorting: false,
        cell: ({ row }: { row: { original: PozycjaStanu } }) => (
          <span className="font-mono text-xs">{row.original.supplierPartNumber ?? ''}</span>
        ),
        meta: { exportValue: (r: PozycjaStanu) => r.supplierPartNumber ?? '' },
      },
      ...(pokazMiejsca
        ? [
            {
              id: 'miejsca',
              header: t('rap.col.places'),
              accessorFn: (r: PozycjaStanu) => r.miejsca,
              cell: ({ row }: { row: { original: PozycjaStanu } }) => (
                <span className="text-xs text-[var(--muted-foreground)]">{row.original.miejsca}</span>
              ),
              meta: { exportValue: (r: PozycjaStanu) => r.miejsca },
            } as ColumnDef<PozycjaStanu>,
          ]
        : []),
      {
        id: 'stan',
        header: t('rap.col.stock'),
        accessorFn: (r) => r.stan,
        cell: ({ row }) =>
          row.original.stan === 0 ? (
            <Badge variant="destructive">
              <PackageX className="size-3" />0
            </Badge>
          ) : (
            nfInt.format(row.original.stan)
          ),
        meta: { align: 'right', sortKey: 'stan', exportValue: (r: PozycjaStanu) => r.stan },
      },
      {
        id: 'pojemnosc',
        header: t('rap.col.capacity'),
        accessorFn: (r) => r.pojemnosc,
        cell: ({ row }) => nfInt.format(row.original.pojemnosc),
        meta: { align: 'right', sortKey: 'pojemnosc', exportValue: (r: PozycjaStanu) => r.pojemnosc },
      },
      {
        /** ⚠️ KOLUMNA, PO KTÓRĄ SIĘGA SIĘ NAJPIERW — stąd pogrubienie. */
        id: 'brakuje',
        header: t('rap.col.missing'),
        accessorFn: (r) => r.brakuje,
        cell: ({ row }) =>
          row.original.brakuje === 0 ? (
            <span className="text-[var(--muted-foreground)]">—</span>
          ) : (
            <strong>{nfInt.format(row.original.brakuje)}</strong>
          ),
        meta: { align: 'right', sortKey: 'brakuje', exportValue: (r: PozycjaStanu) => r.brakuje },
      },
      /**
       * ⚠️ „PRZEGRÓDEK" I „PRÓG KRYTYCZNY" ZDJĘTE Z EKRANU NA ZGŁOSZENIE (31.08.2026): „wywal
       * z domyślnych kolumn «Przegródek» i «Próg krytyczny»". Ten ekran odpowiada na pytanie
       * „co dowieźć", a obie kolumny opisują UŁOŻENIE towaru w automacie — czyli inną robotę
       * i inną osobę.
       *
       * ⚠️ ZNIKAJĄ TEŻ Z PLIKU POBIERANEGO PASKIEM, i to jest zamierzone: przycisk eksportu przy
       * liście składa plik z KOLUMN EKRANU (`meta.exportValue` w `DataTable`), więc zdjęcie
       * kolumny stąd zabiera ją z obu miejsc naraz.
       *
       * ⚠️ Z RAPORTU NIE ZNIKAJĄ. W katalogu API (`KOLUMNY_STANU`) stoją dalej — poza zestawem
       * domyślnym — więc technik wybierze je w oknie „własne kolumny" i zapisze w harmonogramie.
       */
    ],
    [t, pokazMiejsca],
  );

  return (
    <div className="grid gap-2">
      <DataTable<PozycjaStanu>
        title={title}
        columns={columns}
        data={strona}
        total={przefiltrowane.length}
        state={state}
        onStateChange={onStateChange}
        isLoading={isLoading}
        isFetching={isFetching}
        onRefresh={onRefresh}
        rowKey={(r) => r.itemId}
        exportFileName={exportFileName}
        fetchAllRows={async () => przefiltrowane}
        reportKey="stanAutomatow"
        reportGroupId={groupId}
        emptyText={t('rap.empty')}
        actions={
          <Button variant="outline" size="sm" onClick={() => setWlasny(true)}>
            <Columns3 className="size-4" />
            {t('rapw.przycisk')}
          </Button>
        }
      />
      {wlasny && groupId !== null && (
        /**
         * ⚠️ RAPORT WŁASNY ZAWSZE DOTYCZY CAŁEJ WYSPY ALBO WYBRANEGO AUTOMATU Z FILTRA — nie
         * bieżącej karty widoku. Karty („razem" i po automatach) dzielą jeden odczyt z serwera
         * i są sposobem przeglądania, a nie filtrem; plik składa serwer od nowa i zna tylko filtry.
         */
        <WlasnyRaport
          raport="stanAutomatow"
          zadanie={{ groupId, ...(machineId ? { machineId } : {}) }}
          onClose={() => setWlasny(false)}
        />
      )}
      <p className="text-xs text-[var(--muted-foreground)]">{opis}</p>
    </div>
  );
}

/* ───────────────────────────── RAPORT WYDAŃ ───────────────────────────── */

function RaportWydan({
  groupId,
  zlecenie,
  kodWyspy,
}: {
  groupId: string;
  zlecenie: Extract<Zlecenie, { rodzaj: 'wydania' }>;
  kodWyspy: string;
}) {
  const { t } = useI18n();
  const [state, setState] = useState<DataTableState>({ search: '', page: 1, pageSize: 50, sort: 'ts', dir: 'desc' });
  const onStateChange = useCallback((patch: Partial<DataTableState>) => setState((s) => ({ ...s, ...patch })), []);
  const [wlasny, setWlasny] = useState(false);

  const params = useMemo(
    () => ({
      ...state,
      groupId,
      ...(zlecenie.machineId ? { machineId: zlecenie.machineId } : {}),
      ...(zlecenie.employeeId ? { employeeId: zlecenie.employeeId } : {}),
      ...(zlecenie.employeeGroupId ? { employeeGroupId: zlecenie.employeeGroupId } : {}),
      ...(zlecenie.costCenter !== undefined ? { costCenter: zlecenie.costCenter } : {}),
      from: granicaZPola(zlecenie.od, false, zlecenie.odGodz),
      to: granicaZPola(zlecenie.do, true, zlecenie.doGodz),
    }),
    [state, groupId, zlecenie],
  );

  const { data, isPending, isFetching, refetch } = useQuery({
    queryKey: ['raport', 'wydania', params],
    queryFn: () => listTransactions(params),
  });

  /**
   * ⚠️ EKSPORT DOCIĄGA WSZYSTKIE STRONY, bo raport za miesiąc ma zwykle więcej wierszy niż jedna.
   * Limit jest twardy, RÓŻNY PER FORMAT i ZGŁASZANY: cicho ucięty plik wygląda jak kompletny raport.
   */
  const fetchAllRows = useCallback(
    (format: FormatEksportu, postep?: (ile: number, zIlu: number) => void): Promise<TransactionRow[]> =>
      zbierzWszystkieStrony<TransactionRow>({
        limit: LIMIT_EKSPORTU[format],
        postep,
        pobierz: (page, pageSize) => listTransactions({ ...params, page, pageSize }),
        zaDuzo: (total, limit) =>
          new Error(
            t('rap.exportTooBig')
              .replace('{limit}', nfInt.format(limit))
              .replace('{total}', nfInt.format(total))
              .replace('{format}', format.toUpperCase()),
          ),
      }),
    [params, t],
  );

  const columns = useMemo<ColumnDef<TransactionRow>[]>(
    () => [
      {
        id: 'ts',
        header: t('rap.col.when'),
        accessorFn: (r) => r.ts,
        /** ⚠️ Czas ŚCIENNY — bez `toLocaleString`, patrz `lib/czas-sps.ts`. */
        cell: ({ row }) => czasSps(row.original.ts),
        meta: { sortKey: 'ts', exportValue: (r: TransactionRow) => r.ts },
      },
      {
        /** Kolumna „z którego automatu" — o nią było wprost poproszone. */
        id: 'machine',
        header: t('rap.col.machine'),
        accessorFn: (r) => r.machine.name,
        enableSorting: false,
        meta: { exportValue: (r: TransactionRow) => r.machine.name },
      },
      {
        id: 'employee',
        header: t('rap.col.employee'),
        accessorFn: (r) => r.employee?.name ?? '',
        enableSorting: false,
        cell: ({ row }) => (
          <div className="grid">
            {row.original.employee === null ? (
              <span className="text-xs text-[var(--muted-foreground)]">{t('rap.noEmployee')}</span>
            ) : (
              <span>{row.original.employee.name}</span>
            )}
            {/*
              NUMER KARTY Z CHWILI WYDANIA pod nazwiskiem — bo to on odpowiada na pytanie
              „na ktora karte to wydano". Bywa jedyna informacja, jaka zostala: po zmianie numeru
              w SPS wydanie zostaje pod starym, a wlasciciela w katalogu juz nie ma.
            */}
            {row.original.userNumberAtIssue !== null && row.original.userNumberAtIssue !== '' && (
              <span className="font-mono text-xs text-[var(--muted-foreground)]">
                {row.original.userNumberAtIssue}
              </span>
            )}
          </div>
        ),
        meta: { exportValue: (r: TransactionRow) => r.employee?.name ?? '' },
      },
      {
        id: 'userNumberAtIssue',
        header: t('rap.col.card'),
        accessorFn: (r) => r.userNumberAtIssue ?? '',
        enableSorting: false,
        cell: ({ row }) =>
          (row.original.userNumberAtIssue ?? '') === '' ? (
            <span className="text-xs text-[var(--muted-foreground)]">—</span>
          ) : (
            <span className="font-mono text-xs">{row.original.userNumberAtIssue}</span>
          ),
        meta: { exportValue: (r: TransactionRow) => r.userNumberAtIssue ?? '' },
      },
      {
        id: 'item',
        header: t('rap.col.item'),
        accessorFn: (r) => r.item?.name ?? '',
        enableSorting: false,
        cell: ({ row }) =>
          row.original.item === null ? (
            <span className="text-xs text-[var(--muted-foreground)]">{t('rap.noItem')}</span>
          ) : (
            row.original.item.name
          ),
        meta: { exportValue: (r: TransactionRow) => r.item?.name ?? '' },
      },
      {
        /**
         * NUMER CZĘŚCI DOSTAWCY — `Items.SUPPLIERPARTNUMBER`, nazwa po sps-owemu.
         *
         * ⚠️ KOLUMNA JEST TU, A NIE TYLKO W KATALOGU RAPORTU, i to nie jest ozdoba: strażnik
         * `katalog-kolumn.spec.ts` wymaga, żeby domyślny zestaw pliku był DOKŁADNIE kolumnami
         * ekranu w tej samej kolejności. Kolumna domyślna w pliku, a niewidoczna na ekranie,
         * znaczyłaby plik inny niż to, co człowiek przed chwilą oglądał.
         */
        id: 'supplierPartNumber',
        header: t('rap.col.supplierPart'),
        accessorFn: (r) => r.item?.supplierPartNumber ?? '',
        enableSorting: false,
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.item?.supplierPartNumber ?? ''}</span>,
        meta: { exportValue: (r: TransactionRow) => r.item?.supplierPartNumber ?? '' },
      },
      {
        id: 'qty',
        header: t('rap.col.qty'),
        accessorFn: (r) => r.qty,
        cell: ({ row }) => nfInt.format(row.original.qty),
        meta: { align: 'right', sortKey: 'qty', exportValue: (r: TransactionRow) => r.qty },
      },
      {
        id: 'value',
        header: t('rap.col.value'),
        accessorFn: (r) => r.value,
        cell: ({ row }) => nfMoney.format(row.original.value),
        meta: { align: 'right', exportValue: (r: TransactionRow) => r.value },
      },
      {
        id: 'costCenterAtIssue',
        header: t('tx.costCenter'),
        accessorFn: (r) => r.costCenterAtIssue ?? '',
        enableSorting: false,
        cell: ({ row }) =>
          (row.original.costCenterAtIssue ?? '') === '' ? (
            <span className="text-xs text-[var(--muted-foreground)]">{t('tx.noCostCenter')}</span>
          ) : (
            <span className="font-mono text-xs">{row.original.costCenterAtIssue}</span>
          ),
        meta: { exportValue: (r: TransactionRow) => r.costCenterAtIssue ?? '' },
      },
      {
        id: 'transCode',
        header: t('rap.col.code'),
        accessorFn: (r) => r.transCode,
        enableSorting: false,
        cell: ({ row }) => <span className="font-mono text-xs">{row.original.transCode}</span>,
        meta: { exportValue: (r: TransactionRow) => r.transCode },
      },
    ],
    [t],
  );

  return (
    <div className="grid gap-2">
      {data !== undefined && (
        <p className="text-sm">
          {t('rap.issuesSummary')
            .replace('{ile}', nfInt.format(data.total))
            .replace('{szt}', nfInt.format(data.summary.issuedQty))
            .replace('{wartosc}', nfMoney.format(data.summary.issuedValue))}
        </p>
      )}
      <DataTable<TransactionRow>
        title={t('rap.kind.issues')}
        columns={columns}
        data={data?.rows ?? []}
        total={data?.total ?? 0}
        state={state}
        onStateChange={onStateChange}
        isLoading={isPending}
        isFetching={isFetching}
        onRefresh={() => void refetch()}
        rowKey={(r) => r.id}
        exportFileName={`wydania-${kodWyspy}-${zlecenie.od}_${zlecenie.do}`}
        fetchAllRows={fetchAllRows}
        reportKey="transactions"
        reportGroupId={groupId}
        emptyText={t('rap.emptyIssues')}
        actions={
          <Button variant="outline" size="sm" onClick={() => setWlasny(true)}>
            <Columns3 className="size-4" />
            {t('rapw.przycisk')}
          </Button>
        }
      />
      {wlasny && (
        /**
         * ⚠️ FILTRY IDĄ TE SAME, KTÓRE MA NA SOBIE EKRAN — bez `page` i `pageSize`, bo raport bierze
         * cały przefiltrowany zbiór, a nie stronę. Drugi zestaw filtrów w oknie eksportu znaczyłby,
         * że plik może opisywać coś innego niż to, co widać za oknem.
         */
        <WlasnyRaport
          raport="transactions"
          zadanie={(({ page, pageSize, ...reszta }) => reszta)(params)}
          onClose={() => setWlasny(false)}
        />
      )}
      {data?.newestOutsideRange != null && (
        <p className="text-xs text-[var(--muted-foreground)]">
          {t('rap.outsideRange').replace('{kiedy}', czasSps(data.newestOutsideRange))}
        </p>
      )}
    </div>
  );
}
